import {
  COGNITO_CLIENT_ID,
  COGNITO_DOMAIN,
  LOGOUT_REDIRECT_URI,
  OAUTH_SCOPES,
  REDIRECT_URI,
} from '../config';
import { generateCodeChallenge, generateRandomString } from './pkce';

interface TokenSet {
  idToken: string;
  accessToken: string;
  refreshToken: string | null;
  expiresAt: number; // ms epoch
}

const SESSION_KEY = 'ft_tokens';
const VERIFIER_KEY = 'ft_pkce_verifier';
const STATE_KEY = 'ft_oauth_state';

// In-memory cache so a hot path (every API call) doesn't re-parse
// sessionStorage; sessionStorage is still the source of truth across
// reloads (a full-page nav happens for login/logout/callback anyway).
let cached: TokenSet | null = null;

function hostedUiBase(): string {
  return `https://${COGNITO_DOMAIN}`;
}

function loadTokens(): TokenSet | null {
  if (cached) return cached;
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    cached = JSON.parse(raw) as TokenSet;
    return cached;
  } catch {
    return null;
  }
}

function saveTokens(tokens: TokenSet): void {
  cached = tokens;
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(tokens));
}

function clearTokens(): void {
  cached = null;
  sessionStorage.removeItem(SESSION_KEY);
}

export function isAuthenticated(): boolean {
  return loadTokens() !== null;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
      .join(''),
  );
  return JSON.parse(json) as Record<string, unknown>;
}

/**
 * Unverified claims from the current session's ID token - fine for UI
 * decisions (e.g. hiding a "remove yourself" button); the API independently
 * enforces auth server-side on every request regardless of what the client
 * reads here.
 */
export function getIdTokenClaims(): Record<string, unknown> | null {
  const current = loadTokens();
  if (!current) return null;
  try {
    return decodeJwtPayload(current.idToken);
  } catch {
    return null;
  }
}

/** Redirects the browser to the Cognito Hosted UI login page. Never returns. */
export async function redirectToLogin(): Promise<never> {
  const verifier = generateRandomString(64);
  const challenge = await generateCodeChallenge(verifier);
  const state = generateRandomString(32);

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const params = new URLSearchParams({
    client_id: COGNITO_CLIENT_ID,
    response_type: 'code',
    scope: OAUTH_SCOPES,
    redirect_uri: REDIRECT_URI,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
  });

  window.location.assign(`${hostedUiBase()}/oauth2/authorize?${params.toString()}`);
  return new Promise<never>(() => {}); // navigation is in flight; never resolve
}

export function logout(): void {
  clearTokens();
  const params = new URLSearchParams({
    client_id: COGNITO_CLIENT_ID,
    logout_uri: LOGOUT_REDIRECT_URI,
  });
  window.location.assign(`${hostedUiBase()}/logout?${params.toString()}`);
}

interface TokenEndpointResponse {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

async function exchange(body: URLSearchParams): Promise<TokenSet> {
  const res = await fetch(`${hostedUiBase()}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) {
    throw new Error(`token endpoint returned ${res.status}`);
  }
  const data = (await res.json()) as TokenEndpointResponse;
  const previous = loadTokens();
  return {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: data.refresh_token ?? previous?.refreshToken ?? null,
    expiresAt: Date.now() + data.expires_in * 1000,
  };
}

/** Called from the /callback route with the `code` query param. */
export async function handleCallback(code: string, state: string): Promise<void> {
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);

  if (!verifier || !expectedState || state !== expectedState) {
    throw new Error('OAuth state mismatch - please log in again');
  }

  const tokens = await exchange(
    new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: COGNITO_CLIENT_ID,
      code,
      redirect_uri: REDIRECT_URI,
      code_verifier: verifier,
    }),
  );
  saveTokens(tokens);
}

async function refresh(): Promise<TokenSet> {
  const current = loadTokens();
  if (!current?.refreshToken) {
    throw new Error('no refresh token available');
  }
  const tokens = await exchange(
    new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: COGNITO_CLIENT_ID,
      refresh_token: current.refreshToken,
    }),
  );
  saveTokens(tokens);
  return tokens;
}

// 30s of slack so a token doesn't expire mid-flight between this check and
// the API call actually landing.
const EXPIRY_SLACK_MS = 30_000;

/**
 * Returns a usable id token, refreshing first if it's expired/near-expiry.
 * If there's no session (or refresh fails), redirects to login and never
 * returns - callers should `await` this before every authenticated request.
 */
export async function getValidIdToken(): Promise<string> {
  const current = loadTokens();
  if (!current) {
    await redirectToLogin();
  }
  if (current!.expiresAt - EXPIRY_SLACK_MS > Date.now()) {
    return current!.idToken;
  }
  try {
    const refreshed = await refresh();
    return refreshed.idToken;
  } catch {
    clearTokens();
    await redirectToLogin();
    throw new Error('unreachable');
  }
}

/** Called once by the API client after a 401 on an already-fresh token. */
export async function forceReauth(): Promise<never> {
  clearTokens();
  return redirectToLogin();
}

/** Explicit refresh, used by the API client's one 401-retry attempt. */
export async function refreshAccessToken(): Promise<string> {
  const tokens = await refresh();
  return tokens.idToken;
}
